// =============================================================================
// features/sales/campaigns/operate/lease.ts — Lógica PURA do countdown de
// lease (`QueueLeaseIndicator`, doc 26 §6.8/§7). Sem React, sem rede —
// testável isoladamente (mesma convenção de hooks/sales/useCampaigns.ts
// `buildCampaignsQuery`).
//
// Duas fontes de verdade nunca se confundem aqui:
//   - `claimExpiresAt` (o servidor manda a cada claim/heartbeat) é a ÚNICA
//     data usada para decidir "quanto falta"/"expirou";
//   - o heartbeat de rede (`isLeaseLostError`, hooks/sales/useCampaignQueue.ts)
//     é a ÚNICA fonte que decide "o lease foi REALMENTE perdido" (409) — o
//     countdown chegar a zero é só um aviso visual local, não uma prova.
// =============================================================================

export type LeaseUrgency = 'ok' | 'warning' | 'critical' | 'expired';

/** Abaixo desse tanto de segundos restantes o indicador vira "warning" (amarelo). */
const WARNING_THRESHOLD_SECONDS = 90;
/** Abaixo desse tanto vira "critical" (vermelho, mas ainda local — não confirmado pelo servidor). */
const CRITICAL_THRESHOLD_SECONDS = 30;

export interface LeaseCountdown {
  remainingSeconds: number;
  urgency: LeaseUrgency;
  label: string;
}

/**
 * Calcula o estado do countdown a partir de `claimExpiresAt` (ISO) e do
 * instante atual. `claimExpiresAt === null` (nenhum membro reservado ainda)
 * devolve `remainingSeconds: 0` com urgência `expired` — o chamador decide
 * se mostra o indicador nesse caso (normalmente não mostra).
 */
export function computeLeaseCountdown(claimExpiresAt: string | null, now: Date): LeaseCountdown {
  if (claimExpiresAt === null) {
    return { remainingSeconds: 0, urgency: 'expired', label: 'Sem reserva ativa' };
  }

  const expiresAtMs = new Date(claimExpiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) {
    return { remainingSeconds: 0, urgency: 'expired', label: 'Sem reserva ativa' };
  }

  const remainingMs = expiresAtMs - now.getTime();
  const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

  if (remainingSeconds <= 0) {
    return { remainingSeconds: 0, urgency: 'expired', label: 'Reserva expirando — confirmando…' };
  }

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const timeLabel = `${minutes}:${String(seconds).padStart(2, '0')}`;

  if (remainingSeconds <= CRITICAL_THRESHOLD_SECONDS) {
    return { remainingSeconds, urgency: 'critical', label: `${timeLabel} restantes` };
  }
  if (remainingSeconds <= WARNING_THRESHOLD_SECONDS) {
    return { remainingSeconds, urgency: 'warning', label: `${timeLabel} restantes` };
  }
  return { remainingSeconds, urgency: 'ok', label: `${timeLabel} restantes` };
}
